import { useState } from "react";
import { Routes, Route } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import ResultsPage from "./pages/ResultsPage";
import LegalPage from "./pages/LegalPage";
import SharedResultsPage from "./pages/SharedResultsPage";
import DiagnosticsPage from "./pages/DiagnosticsPage";
import PremiumSuccessPage from "./pages/PremiumSuccessPage";
import SavePremiumPage from "./pages/SavePremiumPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { questions } from "./data/questions";
import { ArchetypeScore, TestResult, Option } from "./types";
import {
  initializeScores,
  calculateResult,
  calculatePercentages,
} from "./utils/scoringUtils";
import { useSaveResult } from "./hooks/useSaveResult";
import { useLoadingState } from "./hooks/useLoadingState";
import FeedbackToast from "./components/FeedbackToast";
import { useNavigate } from "react-router-dom";
import RegistrationPage from "./pages/RegistrationPage";
import { UnsubscribePage } from "./pages/UnsubscribePage";

function App() {
  const [currentPage, setCurrentPage] = useState<
    "landing" | "questionnaire" | "registration" | "results"
  >("landing");
  const [scores, setScores] = useState<ArchetypeScore>(initializeScores());
  const [result, setResult] = useState<TestResult | null>(null);
  const [percentages, setPercentages] = useState<Record<string, number>>({});
  const { isLoading, startLoading, stopLoading } = useLoadingState();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const trackGAEvent = (eventName: string, eventParams?: Record<string, any>) => {
    console.log("Tracking GA Event:", eventName, eventParams);
    if (window.gtag) {
      console.log("Call GA Event");
      window.gtag('event', eventName, eventParams || {});
    }
  };

  // Store test completion data before registration
  const [completedTestData, setCompletedTestData] = useState<{
    finalScores: ArchetypeScore;
    selectedOptions: Option[];
    categoryScores: any;
    testResult: TestResult;
    scorePercentages: Record<string, number>;
  } | null>(null);

  const [userData, setUserData] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    employerName: string;
  } | null>(null);

  const { save: saveResult } = useSaveResult();

  // This now handles the test completion and moves to registration
  const handleCompleteTest = async (
    finalScores: ArchetypeScore,
    selectedOptions: Option[],
    categoryScores: any
  ) => {
    try {
      startLoading();
      setError(null);

      // Calculate results but don't save yet
      const testResult = calculateResult(
        finalScores,
        questions,
        selectedOptions
      );
      const scorePercentages = calculatePercentages(finalScores, questions);

      // Store the completed test data
      setCompletedTestData({
        finalScores,
        selectedOptions,
        categoryScores,
        testResult,
        scorePercentages,
      });

      trackGAEvent('complete_test', {
        archetype: testResult?.primaryArchetype?.id
      });

      // Move to registration page
      setCurrentPage("registration");
      
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error processing test completion:", error);
      setError("There was an error processing your test. Please try again.");
    } finally {
      stopLoading();
    }
  };

  // This now handles registration and saves everything to the database
  const handleRegistration = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    employerName: string;
    marketingConsent: boolean;
  }) => {
    if (!completedTestData) {
      setError("Test data not found. Please retake the test.");
      setCurrentPage("questionnaire");
      return;
    }

    try {
      startLoading();
      setError(null);

      const {
        finalScores,
        selectedOptions,
        categoryScores,
        testResult,
        scorePercentages,
      } = completedTestData;

      // Save user data and test results to database
      const saveData = await saveResult(
        {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          job_title: data.jobTitle,
          employer_name: data.employerName,
          marketing_consent: data.marketingConsent,
        },
        {
          primaryArchetype: testResult.primaryArchetype.id,
          secondaryArchetype: testResult.secondaryArchetype.id,
          jaguar_score: finalScores.jaguar,
          llama_score: finalScores.llama,
          quetzal_score: finalScores.quetzal,
          colibri_score: finalScores.colibri,
          coqui_score: finalScores.coqui,
          buho_score: finalScores.buho,
          score_data: scorePercentages,
          condor_score: finalScores.condor || 0,
        },
        categoryScores,
        selectedOptions
      );

      if (!saveData) {
        throw new Error("Failed to save test results");
      }

      // Set user data
      setUserData({
        id: saveData.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        jobTitle: data.jobTitle,
        employerName: data.employerName,
      });

      const resultWithMetadata: TestResult = {
        ...testResult,
        testResultId: saveData.id,
        userName: `${data.firstName} ${data.lastName}`,
        email: data.email,
        categoryScores: categoryScores,
      };

      trackGAEvent('complete_registration', {
        archetype: testResult?.primaryArchetype?.id,
        userId: saveData?.id,
      });

      setScores(finalScores);
      setResult(resultWithMetadata);
      setPercentages(scorePercentages);

      // Navigate to results page
      navigate(`/results-report?id=${saveData.id}`, {
        state: {
          result: resultWithMetadata,
          percentages: scorePercentages,
          scores: finalScores,
          categoryScores: categoryScores,
        },
      });

      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error saving registration data:", error);
      setError("There was an error saving your information. Please try again.");
    } finally {
      stopLoading();
    }
  };

  const handleRestartTest = () => {
    setScores(initializeScores());
    setResult(null);
    setError(null);
    setCompletedTestData(null);
    setUserData(null);
    setCurrentPage("landing");
    window.scrollTo(0, 0);
  };

  const getRedirect = () => {
    if (currentPage === "questionnaire") {
      return (
        <QuestionnairePage
          questions={questions}
          onComplete={handleCompleteTest}
          initialScores={scores}
          isLoading={isLoading}
          error={error}
        />
      );
    }

    if (currentPage === "registration") {
      return (
        <RegistrationPage
          onSubmit={handleRegistration}
          isLoading={isLoading}
          error={error}
        />
      );
    }

    if (currentPage === "results" && result) {
      return <ResultsPage result={result} onRestart={handleRestartTest} />;
    }

    return (
      <LandingPage
        onStartTest={() => setCurrentPage("questionnaire")}
      />
    );
  };

  return (
    <ErrorBoundary>
      <div className="font-sans">
        <Routes>
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/shared-results" element={<SharedResultsPage />} />
          <Route path="/diagnostics" element={<DiagnosticsPage />} />
          <Route path="/results-report" element={<ResultsPage />} />
          <Route path="/save-result-premium" element={<SavePremiumPage />} />
          <Route path="/premium-success" element={<PremiumSuccessPage />} />
          <Route path="/unsubscribe/:email" element={<UnsubscribePage />} /> {/* <-- Added route */}
          <Route path="/" element={getRedirect()} />
        </Routes>
        {error && (
          <FeedbackToast
            message={error}
            type="error"
            onClose={() => setError(null)}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;
